import { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import { useAuthContext } from './useAuthContext';

export const useFavorites = () => {
  const { session } = useAuthContext();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!session) {
      setFavorites([]);
      setLoading(false);
      return;
    }

    const fetchFavorites = async () => {
      try {
        setLoading(true);
        console.log('Fetching favorites from Supabase...');

        // Fetch favorites with product details joined
        const { data, error: fetchError } = await supabase
          .from('favorites')
          .select(`
            id,
            created_at,
            product_id,
            product:products(
              id,
              name,
              price,
              currency,
              images,
              brand_id,
              dis,
              brand:brands(id, name, active)
            )
          `)
          .eq('profile_id', session.user.id)
          .order('created_at', { ascending: false });

        console.log('Favorites response - data:', data, 'error:', fetchError);

        if (fetchError) {
          console.error('Error fetching favorites:', fetchError);
          throw fetchError;
        }

        if (data) {
          // Filter out favorites from inactive brands
          const activeBrandFavorites = data.filter(fav => {
            return fav.product?.brand?.active === true;
          });

          console.log(`Found ${data.length} total favorites, ${activeBrandFavorites.length} from active brands`);
          setFavorites(activeBrandFavorites);
        } else {
          console.log('No favorites found');
          setFavorites([]);
        }
      } catch (err) {
        console.error('Error fetching favorites:', err);
        setError(err.message);
        setFavorites([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFavorites();
  }, [session]);

  const addFavorite = async (productId) => {
    if (!session) {
      throw new Error('You must be logged in to add favorites');
    }

    try {
      console.log('Adding favorite:', productId);

      const { data, error: insertError } = await supabase
        .from('favorites')
        .insert({
          profile_id: session.user.id,
          product_id: productId
        })
        .select()
        .single();

      console.log('Add favorite response - data:', data, 'error:', insertError);

      if (insertError) {
        throw insertError;
      }

      // Refresh favorites list
      const fetchFavorites = async () => {
        const { data: newData } = await supabase
          .from('favorites')
          .select(`
            id,
            created_at,
            product_id,
            product:products(
              id,
              name,
              price,
              currency,
              images,
              brand_id,
              dis,
              brand:brands(id, name, active)
            )
          `)
          .eq('profile_id', session.user.id)
          .order('created_at', { ascending: false });

        // Filter out favorites from inactive brands
        const activeBrandFavorites = newData?.filter(fav => {
          return fav.product?.brand?.active === true;
        }) || [];

        setFavorites(activeBrandFavorites);
      };

      await fetchFavorites();

      return data;
    } catch (error) {
      console.error('Error adding favorite:', error);
      return error;
    }
  };

  const removeFavorite = async (productId) => {
    if (!session) {
      throw new Error('You must be logged in to remove favorites');
    }

    try {
      console.log('Removing favorite:', productId);

      const { error: deleteError } = await supabase
        .from('favorites')
        .delete()
        .eq('profile_id', session.user.id)
        .eq('product_id', productId);

      console.log('Remove favorite response - error:', deleteError);

      if (deleteError) {
        throw deleteError;
      }

      // Remove from local state
      setFavorites(prevFavorites =>
        prevFavorites.filter(fav => fav.product_id !== productId)
      );

      return true;
    } catch (err) {
      console.error('Error removing favorite:', err);
      throw err;
    }
  };

  const isFavorite = (productId) => {
    return favorites.some(fav => fav.product_id === productId);
  };

  return {
    favorites,
    loading,
    error,
    addFavorite,
    removeFavorite,
    isFavorite
  };
};
